import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import CopyableValue from "../common/CopyableValue";
import PpLogo from "../common/PpLogo";
import { EvidenceOrderSourceKind } from "../../types/evidence";

// The agent works this page beside the store's own admin, and these two are what they carry across
// to find the case there. Typing them back out by hand is the step this removes.
export default function CaseLookupRows({ bank, orderName, orderSource, customerEmail, paypalBuyerEmail, generated, storefront }: {
  /** A bank case only: the bank and its dispute ID, which is what the agent finds the case by in the portal. */
  bank?: string | null;
  orderName: string | null;
  /** Which system to search: the same number means nothing in the other one. */
  orderSource: EvidenceOrderSourceKind | null;
  customerEmail: string | null;
  paypalBuyerEmail?: string | null;
  generated?: boolean;
  /** The storefront row's value: which storefront the document prints, and the control to pick it. */
  storefront?: ReactNode;
}) {
  const email = customerEmail ?? paypalBuyerEmail;
  if (!bank && !orderName && !email && !storefront) return null;

  // One grid, so every label and every value starts on the same line, as in the builder's mock.
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "76px minmax(0, 1fr)", rowGap: "9px", columnGap: "12px", alignItems: "center", fontSize: 13, lineHeight: "20px" }}>
      {bank && <LookupRow label="Bank" value={bank} />}
      {orderName && <LookupRow label="Order" value={orderName} mark={orderSource && <PpLogo ppType={orderSource} size={18} />} />}
      {email && <LookupRow label="Customer" value={email} caption={customerEmail ? undefined : generated ? "from PayPal" : "from PayPal · the store's email shows after Generate"} />}
      {storefront && (
        <>
          <RowLabel label="Storefront" />
          {storefront}
        </>
      )}
    </Box>
  );
}

function RowLabel({ label }: { label: string }) {
  return <Box sx={{ color: "#667085" }}>{label}</Box>;
}

function LookupRow({ label, value, mark, caption }: { label: string; value: string; mark?: ReactNode; caption?: string }) {
  return (
    <>
      <RowLabel label={label} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          {mark}
          <CopyableValue
            text={value}
            copyTitle={`Copy ${label.toLowerCase()}`}
            iconFontSize={14}
            textSx={{ fontWeight: 600, color: "#1d2939", wordBreak: "break-word" }}
          />
        </Box>
        {caption && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{caption}</Typography>}
      </Box>
    </>
  );
}
